import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Image } from "react-bootstrap";
import { useParams } from "react-router-dom";
import "./RequestDetails.scss";
import logo from "../assets/images/logo-sidebar.svg";
import offerCard from "../assets/images/offerCard.png";
import offerPackage from "../assets/images/offerPackage.svg";
import sourceIcon from "../assets/images/sourceIcon.svg";
import flagCheckered from "../assets/images/FlagCheckered.svg";
import redirectToMap from "../assets/images/RedirectToMap.svg";
import helpIcon from "../assets/images/helpIcon.svg";
import profileImg1 from "../assets/images/user.png";
import delivaApiService from "../common/services/apiService";
import CONSTANTS from "../common/helpers/Constants";
import moment from "moment";

const RequestDetails = () => {
  const { id } = useParams(); // Get ID from URL path parameter
  const [requestDetails, setRequestDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRequestDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await delivaApiService("getPublicRequestDetail", {
        requestId: id,
      });
      console.log(
        "API Response for Request Details:",
        response?.data?.resourceData,
      );
      if (response && response.data && response.data.resourceData) {
        setRequestDetails(response.data.resourceData);
      }
    } catch (error) {
      console.error("Error fetching request details:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequestDetails();
  }, [fetchRequestDetails]);

  const orderStatus = (status) => {
    if (status === CONSTANTS.ORDER_STATUS.TRANSIT) {
      return "In transit";
    } else if (status === CONSTANTS.ORDER_STATUS.ACCEPTED) {
      return "Accepted bid";
    } else if (status === CONSTANTS.ORDER_STATUS.DELIVERED) {
      return "Delivered";
    } else if (status === CONSTANTS.ORDER_STATUS.CANCELLED) {
      return "Cancelled";
    } else if (status === CONSTANTS.ORDER_STATUS.REJECTED) {
      return "Pickup rejected";
    }
  };

  const redirectToHelp = () => {
    // Implement your help page redirection logic here
    window.open("https://delivahere.com/contact/", "_blank");
  };

  const redirectToDownloadApp = () => {
    const androidStoreUrl = `https://play.google.com/store/apps/details?id=com.deliva.customer`;
    const iosStoreUrl = `https://apps.apple.com/in/app/delivahere/id6499013194`;
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    window.location.href = isIOS ? iosStoreUrl : androidStoreUrl;
  };

  const formatPhoneNumber = (num) => {
    // Remove all non-digits except +
    num = num.replace(/[^+\d]/g, "");

    // Extract country code (+234) and remaining digits
    const country = num.slice(0, 4); // +234
    const rest = num.slice(4); // 5868658565

    // Format groups: 3 - 3 - 4
    const part1 = rest.slice(0, 3);
    const part2 = rest.slice(3, 6);
    const part3 = rest.slice(6);

    return `${country} ${part1} ${part2} ${part3}`;
  };

  return (
    <div className="delivery-tracking-page">
      {/* Header */}
      <header className="delivery-header">
        <Container fluid>
          <Row className="align-items-center">
            <Col xs={5} md={6}>
              <div className="logo">
                <img src={logo} alt="DelivaHere" className="logo-image" />
              </div>
            </Col>
            <Col xs={7} md={6}>
              <div className="header-buttons">
                <div className="help-btn" onClick={redirectToHelp}>
                  <img src={helpIcon} alt="Help" />
                  Help
                </div>
                <button
                  className="download-btn"
                  onClick={redirectToDownloadApp}
                >
                  Download app
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </header>

      {/* Promo Cards Carousel */}
      <section className="promo-carousel">
        {loading ? (
          <div className="promo-cards-container">
            <div className="promo-card-skeleton">Loading offers...</div>
          </div>
        ) : (
          requestDetails?.inAppMessages.length > 0 && (
            <div className="promo-cards-container">
              {requestDetails?.inAppMessages.map((card) => (
                <div key={card.id} className="promo-card">
                  <div className="promo-content">
                    <img
                      src={offerPackage}
                      alt="Offer Package"
                      className="promo-package-icon"
                    />
                    <div className="promo-text">
                      <h3 className="promo-title">
                        {card?.discountType === "PERCENTAGE_DISCOUNT"
                          ? `${card.discountPercent}% OFF`
                          : card?.discountType === "FREE_DELIVERY"
                            ? "FREE DELIVERY"
                            : "WIN CASH AMOUNT"}
                      </h3>
                      <p className="promo-date">{`${card.promotionStartDate ? moment(card.promotionStartDate).format("MMM DD, YYYY") : "--"} - ${card.promotionEndDate ? moment(card.promotionEndDate).format("MMM DD, YYYY") : "ONGOING"}`}</p>
                    </div>
                  </div>
                  <img
                    src={offerCard}
                    alt="Offer Card Background"
                    className="promo-card-bg"
                  />
                </div>
              ))}
            </div>
          )
        )}
      </section>

      {/* Main Content */}
      <section className="main-content">
        <Container>
          {/* Gift Items Section */}
          <div className="gift-items-section">
            <div className="section-header">
              <div className="header-left">
                <h4 className="section-title">Gift items</h4>
                <p className="order-id">
                  {requestDetails?.docketNumber
                    ? `#${requestDetails?.docketNumber}`
                    : ""}
                </p>
              </div>
              <span
                className={`status-badge ${
                  requestDetails?.status === CONSTANTS.ORDER_STATUS.DELIVERED
                    ? "text-green"
                    : requestDetails?.status === CONSTANTS.ORDER_STATUS.TRANSIT
                      ? "text-orange"
                      : "text-red"
                }`}
              >
                {orderStatus(requestDetails?.status)?.toUpperCase()}
              </span>
            </div>

            {/* Row 1: Expected delivery | Source | Destination */}
            <div className="delivery-source-destination">
              <div className="info-column">
                <span className="info-label">Expected delivery</span>
                <span className="info-value">
                  {requestDetails?.expectedDeliveryDate
                    ? moment(requestDetails?.expectedDeliveryDate).format(
                        "MMM D | hh:mm A",
                      )
                    : "--"}
                </span>
              </div>
              <div className="info-column">
                <span className="info-label">Source</span>
                <span className="info-value">
                  {requestDetails?.sourceLocation || "--"}
                </span>
              </div>
              <div className="info-column">
                <span className="info-label">Destination</span>
                <span className="info-value">
                  {requestDetails?.destinationLocation || "--"}
                </span>
              </div>
            </div>

            <hr className="section-divider" />

            {/* Row 2: Weight | Source description */}
            <div className="weight-description-row">
              <div className="info-column">
                <span className="info-label">Weight</span>
                <span className="info-value">
                  {requestDetails?.weight || "--"}
                </span>
              </div>
              <div className="info-column">
                <span className="info-label">Additional Notes</span>
                <span className="info-value">
                  {requestDetails?.description || "--"}
                </span>
              </div>
            </div>

            <hr className="section-divider" />

            {/* Row 3: Photos */}
            <div className="photos-section">
              <span className="photos-label">Package preview</span>
              <div className="photos-grid">
                {requestDetails?.requestMediaUrls &&
                requestDetails.requestMediaUrls.length > 0 ? (
                  requestDetails.requestMediaUrls.map((photo) => (
                    <div key={photo.originalMediaUrl} className="photo-item">
                      <img src={photo.originalMediaUrl} alt="Package" />
                    </div>
                  ))
                ) : (
                  <span style={{ color: "#999", fontSize: "0.875rem" }}>
                    No photos available
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Section - Two Columns */}
          <Row>
            {(requestDetails?.status === CONSTANTS.ORDER_STATUS.TRANSIT ||
              requestDetails?.status === CONSTANTS.ORDER_STATUS.DELIVERED) && (
              <Col lg={6}>
                {/* Tracking Package */}
                <div className="tracking-card">
                  <h5 className="card-title">Tracking package</h5>
                  <div className="follow-map">
                    <button className="map-btn">
                      Follow by map
                      <img
                        src={redirectToMap}
                        alt="Redirect to map"
                        className="map-icon"
                      />
                    </button>
                  </div>
                  <div className="tracking-progress">
                    <div className="progress-bar-container">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width:
                              requestDetails?.status ===
                              CONSTANTS.ORDER_STATUS.DELIVERED
                                ? "100%"
                                : "50%",
                          }}
                        ></div>
                      </div>
                      <div className="progress-icons">
                        <div className="progress-icon start active">
                          <img
                            src={sourceIcon}
                            alt="Source"
                            className="icon-image"
                          />
                        </div>
                        <div className="progress-icon end">
                          <img
                            src={flagCheckered}
                            alt="Destination"
                            className="icon-image"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="tracking-labels">
                      <div className="tracking-label active">
                        Picked up by DA
                      </div>
                      <div className="tracking-label">Received by RC</div>
                    </div>
                  </div>
                </div>
              </Col>
            )}

            <Col lg={6}>
              {/* Delivery Agent Details */}
              <div className="agent-card">
                <h5 className="card-title">Delivery partner details</h5>
                <div className="agent-info">
                  <div className="agent-avatar">
                    <Image
                      src={
                        requestDetails?.partnerProfileImageUrl
                          ? requestDetails?.partnerProfileImageUrl
                          : profileImg1
                      }
                      className="img-fluid rounded-circle"
                    />
                  </div>
                  <div className="agent-details">
                    <div className="agent-row">
                      <div className="agent-field">
                        <span className="detail-label">Name</span>
                        <span className="detail-value">
                          {requestDetails?.partnerName || "--"}
                        </span>
                      </div>
                      <div className="agent-field">
                        <span className="detail-label">Contact number</span>
                        <span className="detail-value">
                          {requestDetails?.partnerContact
                            ? formatPhoneNumber(requestDetails?.partnerContact)
                            : "--"}
                        </span>
                      </div>
                    </div>
                    <hr className="agent-divider" />
                    <div className="agent-row">
                      <div className="agent-field full-width">
                        <span className="detail-label">Email id</span>
                        <span className="detail-value">
                          {requestDetails?.partnerEmail || "--"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Bottom Row - Sender and Receiver Details */}
          <Row>
            <Col lg={6}>
              {/* Sender's Details */}
              <div className="details-card">
                <h5 className="card-title">Sender's details</h5>
                <div className="details-content">
                  <div className="details-row">
                    <div className="detail-field">
                      <span className="detail-label">Name</span>
                      <span className="detail-value">
                        {requestDetails?.senderName || "--"}
                      </span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Contact number</span>
                      <span className="detail-value">
                        {requestDetails?.senderContact
                          ? formatPhoneNumber(requestDetails?.senderContact)
                          : "--"}
                      </span>
                    </div>
                  </div>
                  <hr className="details-divider" />
                  <div className="details-row">
                    <div className="detail-field full-width">
                      <span className="detail-label">Address</span>
                      <span className="detail-value">
                        {requestDetails?.senderAddress || "--"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              {/* Receiver's Details */}
              <div className="details-card">
                <h5 className="card-title">Receiver's details</h5>
                <div className="details-content">
                  <div className="details-row">
                    <div className="detail-field">
                      <span className="detail-label">Name</span>
                      <span className="detail-value">
                        {requestDetails?.receiverName || "--"}
                      </span>
                    </div>
                    <div className="detail-field">
                      <span className="detail-label">Contact number</span>
                      <span className="detail-value">
                        {requestDetails?.receiverContactNo
                          ? formatPhoneNumber(requestDetails?.receiverContactNo)
                          : "--"}
                      </span>
                    </div>
                  </div>
                  <hr className="details-divider" />
                  <div className="details-row">
                    <div className="detail-field full-width">
                      <span className="detail-label">Address</span>
                      <span className="detail-value">
                        {requestDetails?.destinationLocation || "--"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default RequestDetails;
